/* luxury-cart.js — AJAX cart drawer.
 *
 * Renders from /cart.js rather than the Section Rendering API. A section
 * living inside the footer group has a compound id that is awkward to
 * address, and building from JSON means one code path for add, change and
 * remove instead of three round trips fetching HTML.
 *
 * Money runs through the shop's own money_format, so the drawer reads
 * "Rs. 2,999.00" exactly as the rest of the store does.
 *
 * Progressive enhancement: with JS unavailable every Add to Bag still posts
 * to /cart normally and the cart link still opens the cart page.
 */
(function () {
  'use strict';

  var root = document.querySelector('[data-cart-drawer]');
  if (!root) return;

  var cfg = {};
  try { cfg = JSON.parse(root.getAttribute('data-config')) || {}; } catch (e) { cfg = {}; }

  var itemsEl = root.querySelector('[data-cart-items]');
  var emptyEl = root.querySelector('[data-cart-empty]');
  var footEl = root.querySelector('[data-cart-foot]');
  var countEl = root.querySelector('[data-cart-count]');
  var subtotalEl = root.querySelector('[data-cart-subtotal]');

  /* ---- Bind the header cart control -----------------------------------
   * The header renders a plain link to /cart, and Dawn's own drawer
   * attribute points at a CartDrawer element this theme does not have — so
   * the click fell through and the cart page opened.
   *
   * Rather than intercepting clicks and racing the shared drawer handler,
   * the link is given the opener attribute outright. luxury-drawer.js then
   * owns it like every other drawer trigger: focus trap, Escape, scroll
   * lock and focus return all come for free.
   */
  function bindCartTriggers(scope) {
    var links = (scope || document).querySelectorAll(
      '.luxury-header a[href$="/cart"], .luxury-header__actions a[href$="/cart"], [data-cart-open]'
    );
    Array.prototype.forEach.call(links, function (link) {
      link.setAttribute('data-drawer-open', 'luxury-cart-drawer');
      link.setAttribute('aria-expanded', 'false');
    });
  }

  bindCartTriggers();
  document.addEventListener('shopify:section:load', function (e) { bindCartTriggers(e.target); });

  /* ---- Money ---------------------------------------------------------- */
  function group(cents, decimals) {
    var n = cents / 100;
    return n.toLocaleString(cfg.locale || 'en-IN', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    });
  }

  function money(cents) {
    var fmt = cfg.moneyFormat || '{{amount}}';
    return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
      if (token.indexOf('no_decimals') !== -1) return group(cents, 0);
      return group(cents, 2);
    });
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function sized(url, w) {
    if (!url) return '';
    return url.replace(/(\.[a-z]+)(\?|$)/i, '_' + w + 'x$1$2');
  }

  /* ---- Render --------------------------------------------------------- */
  function render(cart) {
    if (countEl) countEl.textContent = cart.item_count;
    updateHeaderCount(cart.item_count);

    if (!cart.item_count) {
      if (itemsEl) itemsEl.innerHTML = '';
      if (emptyEl) emptyEl.hidden = false;
      if (footEl) footEl.hidden = true;
      return;
    }

    if (emptyEl) emptyEl.hidden = true;
    if (footEl) footEl.hidden = false;

    var html = '';
    cart.items.forEach(function (item, i) {
      var line = i + 1;

      var variant = '';
      if (item.options_with_values) {
        variant = item.options_with_values
          .filter(function (o) { return o.value && o.value !== 'Default Title'; })
          .map(function (o) { return esc(o.value); })
          .join(' / ');
      }

      var price = item.original_line_price > item.final_line_price
        ? '<del>' + money(item.original_line_price) + '</del>' + money(item.final_line_price)
        : money(item.final_line_price);

      html +=
        '<li class="luxury-cart__item" data-line="' + line + '">' +
          '<a class="luxury-cart__media" href="' + esc(item.url) + '">' +
            (item.image ? '<img src="' + esc(sized(item.image, 300)) + '" alt="' + esc(item.product_title) + '" loading="lazy">' : '') +
          '</a>' +
          '<div class="luxury-cart__info">' +
            '<div class="luxury-cart__row">' +
              '<a class="luxury-cart__name" href="' + esc(item.url) + '">' + esc(item.product_title) + '</a>' +
              '<span class="luxury-cart__price">' + price + '</span>' +
            '</div>' +
            (variant ? '<span class="luxury-cart__variant">' + variant + '</span>' : '') +
            '<div class="luxury-cart__controls">' +
              '<span class="luxury-cart__qty">' +
                '<button type="button" data-cart-qty="down" aria-label="Decrease quantity">&minus;</button>' +
                '<span>' + item.quantity + '</span>' +
                '<button type="button" data-cart-qty="up" aria-label="Increase quantity">+</button>' +
              '</span>' +
              '<button class="luxury-cart__remove" type="button" data-cart-remove>Remove</button>' +
            '</div>' +
          '</div>' +
        '</li>';
    });

    if (itemsEl) itemsEl.innerHTML = html;
    if (subtotalEl) subtotalEl.innerHTML = money(cart.total_price);
  }

  function updateHeaderCount(count) {
    var links = document.querySelectorAll('.luxury-header a[href$="/cart"]');
    Array.prototype.forEach.call(links, function (link) {
      var badge = link.querySelector('.luxury-icon-btn__count');
      if (count > 0) {
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'luxury-icon-btn__count';
          badge.setAttribute('aria-hidden', 'true');
          link.appendChild(badge);
        }
        badge.textContent = count;
      } else if (badge) {
        badge.remove();
      }
      link.setAttribute('aria-label', 'Cart, ' + count + (count === 1 ? ' item' : ' items'));
    });
  }

  /* ---- Requests ------------------------------------------------------- */
  function fetchCart() {
    return fetch(cfg.cartUrl + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function refresh(open) {
    return fetchCart().then(function (cart) {
      render(cart);
      if (open && window.LuxuryDrawer) window.LuxuryDrawer.open(root);
      return cart;
    });
  }

  function change(line, quantity, itemEl) {
    if (itemEl) itemEl.setAttribute('data-busy', 'true');
    return fetch(cfg.changeUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ line: line, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(render)
      .catch(function () { if (itemEl) itemEl.removeAttribute('data-busy'); });
  }

  /* Opened by the shared drawer controller — pull fresh contents, in case
     the cart changed in another tab. */
  document.addEventListener('luxury:drawer:open', function (e) {
    if (e.detail && e.detail.drawer === root) {
      fetchCart().then(render);
    }
  });

  /* ---- Add to cart ----------------------------------------------------- */
  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form.action || form.action.indexOf('/cart/add') === -1) return;

    e.preventDefault();

    var btn = form.querySelector('[type="submit"]');
    var label = btn ? btn.textContent : '';
    if (btn) { btn.disabled = true; btn.textContent = cfg.addingText || 'Adding\u2026'; }

    fetch(cfg.addUrl, { method: 'POST', body: new FormData(form), headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res.status) {
          window.alert(res.description || res.message);
          return;
        }
        return refresh(true);
      })
      .catch(function () { form.submit(); })
      .finally(function () {
        if (btn) { btn.disabled = false; btn.textContent = label; }
      });
  });

  /* ---- Line controls --------------------------------------------------- */
  document.addEventListener('click', function (e) {
    var item = e.target.closest && e.target.closest('.luxury-cart__item');
    if (!item) return;

    var line = parseInt(item.getAttribute('data-line'), 10);

    var qtyBtn = e.target.closest('[data-cart-qty]');
    if (qtyBtn) {
      var current = parseInt(item.querySelector('.luxury-cart__qty span').textContent, 10) || 1;
      var next = qtyBtn.getAttribute('data-cart-qty') === 'up' ? current + 1 : current - 1;
      change(line, Math.max(0, next), item);
      return;
    }

    if (e.target.closest('[data-cart-remove]')) {
      change(line, 0, item);
    }
  });

  /* ---- Order note ------------------------------------------------------ */
  document.addEventListener('click', function (e) {
    var t = e.target.closest && e.target.closest('[data-cart-note-toggle]');
    if (!t) return;
    var panel = root.querySelector('[data-cart-note]');
    if (!panel) return;
    var open = panel.getAttribute('data-open') === 'true';
    panel.setAttribute('data-open', String(!open));
    if (!open) { var f = panel.querySelector('textarea'); if (f) f.focus(); }
  });

  root.addEventListener('change', function (e) {
    if (!e.target.matches('[data-cart-note] textarea')) return;
    fetch(cfg.updateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ note: e.target.value })
    });
  });

  fetchCart().then(render);
})();
